import type { CandidatePreferenceSeedDto } from "@artha/api-contracts";

import type { CandidateIdentityStorage } from "./storage.types";
export type { CandidateIdentityStorage } from "./storage.types";
import { CANDIDATE_IDENTITY_STORAGE_KEY, LEGACY_JOB_ALERT_STORAGE_KEY, EMPTY_IDENTITY } from "./storage.constants";
export { CANDIDATE_IDENTITY_STORAGE_KEY } from "./storage.constants";
const readStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is string =>
          typeof entry === "string" && entry.trim().length > 0,
      )
    : [];

const readCandidatePreferences = (
  value: unknown,
): CandidatePreferenceSeedDto | null => {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  const preferences = {
    titles: readStringArray(input.titles),
    skills: readStringArray(input.skills),
    keywords: readStringArray(input.keywords),
  };
  return preferences.titles.length ||
    preferences.skills.length ||
    preferences.keywords.length
    ? preferences
    : null;
};

export const readCandidateIdentity = (): CandidateIdentityStorage => {
  if (typeof window === "undefined") return EMPTY_IDENTITY;
  try {
    const raw = window.localStorage.getItem(CANDIDATE_IDENTITY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CandidateIdentityStorage>;
      return {
        externalId:
          typeof parsed.externalId === "string" ? parsed.externalId : null,
        resumePersonalized: parsed.resumePersonalized === true,
        candidatePreferences: readCandidatePreferences(
          parsed.candidatePreferences,
        ),
      };
    }
    const legacyRaw = window.localStorage.getItem(LEGACY_JOB_ALERT_STORAGE_KEY);
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw) as { externalId?: unknown };
      if (typeof parsed.externalId === "string") {
        return {
          externalId: parsed.externalId,
          resumePersonalized: false,
          candidatePreferences: null,
        };
      }
    }
  } catch {
    return EMPTY_IDENTITY;
  }
  return EMPTY_IDENTITY;
};

export const writeCandidateIdentity = (identity: CandidateIdentityStorage) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    CANDIDATE_IDENTITY_STORAGE_KEY,
    JSON.stringify(identity),
  );
};

export const clearCandidateIdentity = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(CANDIDATE_IDENTITY_STORAGE_KEY);
};
